import L from "leaflet";

let startPoint = null;
let endPoint = null;
let navigationActivated = false;
let routeLayer = null;
let tempStartMarker = null;
let tempEndMarker = null;

// Mapbox token should be available globally
const mapboxToken = window.mpsString?.mapbox_token || "";

/**
 * Safely call bounce() method on a marker
 * @param {L.Marker} marker - The Leaflet marker
 * @param {Object} options - Bounce options (duration, height, etc.)
 * @returns {boolean} - True if bounce was called successfully, false otherwise
 */
function safeBounce(marker, options = { duration: 500, height: 100 }) {
  try {
    if (!marker) {
      console.warn("safeBounce: Marker is null or undefined");
      return false;
    }

    if (typeof marker.bounce === "function") {
      marker.bounce(options);
      return true;
    } else {
      console.warn(
        "safeBounce: bounce() method not available on marker. Smooth Marker Bouncing plugin may not be loaded.",
      );
      return false;
    }
  } catch (error) {
    console.error("safeBounce: Error calling bounce() method:", error);
    return false;
  }
}

function clearTempMarkers() {
  if (routeLayer) {
    window.singleLocationMap.removeLayer(routeLayer);
    routeLayer = null;
  }
  if (tempStartMarker) {
    window.singleLocationMap.removeLayer(tempStartMarker);
    tempStartMarker = null;
  }
  if (tempEndMarker) {
    window.singleLocationMap.removeLayer(tempEndMarker);
    tempEndMarker = null;
  }
}

async function fetchAndDisplayRoute(start, end) {
  if (!mapboxToken) {
    console.error("Mapbox token not available");
    return;
  }

  const directionsUrl = `https://api.mapbox.com/directions/v5/mapbox/driving/${start.lng},${start.lat};${end.lng},${end.lat}?alternatives=true&geometries=geojson&steps=true&overview=full&language=vi&access_token=${mapboxToken}`;

  try {
    const response = await fetch(directionsUrl);
    const json = await response.json();

    const routeLine = json.routes[0].geometry;
    routeLayer = L.geoJSON(routeLine, {
      style: {
        color: "#0019ff",
        weight: 8,
        opacity: 0.75,
      },
    }).addTo(window.singleLocationMap);

    routeLayer.on("add", function () {
      window.singleLocationMap.fitBounds(routeLayer.getBounds(), {
        padding: [30, 30],
      });
    });
    window.singleLocationMap.fitBounds(routeLayer.getBounds());

    const distance = (json.routes[0].distance / 1000).toFixed(2);
    const totalSeconds = json.routes[0].duration;
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const durationString = `${hours > 0 ? hours + " hour" + (hours > 1 ? "s" : "") + ", " : ""}${minutes} minutes`;

    let instructionsHtml = `
        <div class="route-header grid grid-cols-[30px_auto] items-center gap-2 mt-4 mb-2 text-sm p-4 rounded bg-gray-800">
            <div class="icon"></div>
            <div>
            <p>${durationString}</p>
            <p>${distance} km</p>
            </div>
        </div>
        <div class="route-step h-max text-sm p-4 rounded bg-gray-800 cursor-pointer hover:bg-gray-700">
        <h4 class="text-sm">Click to view steps</h4>
        <ol class="list-decimal step-list">`;

    json.routes[0].legs[0].steps.forEach(function (step) {
      instructionsHtml += `<li>${step.maneuver.instruction}</li>`;
    });

    instructionsHtml += `</ol></div>`;

    const instructionsElement = document.getElementById("instructions");
    if (instructionsElement) {
      instructionsElement.innerHTML = instructionsHtml;
    }
  } catch (error) {
    console.error("Error fetching route:", error);
  }
}

/**
 * Handle marker click for navigation
 * @param {L.Marker} marker - The clicked marker
 * @param {L.LatLng} markerLatLng - The marker's position
 * @param {string} tooltipContent - The tooltip content
 */
export function handleMarkerClick(marker, markerLatLng, tooltipContent) {
  safeBounce(marker);

  if (navigationActivated) {
    // Reset existing points and markers if end point is already set
    if (endPoint) {
      clearTempMarkers();
      startPoint = null;
      endPoint = null;
    }

    let inputValue;
    if (tooltipContent) {
      // Inline decoding of HTML entities in the tooltip content
      const txt = document.createElement("textarea");
      txt.innerHTML = tooltipContent;
      inputValue = txt.value;
    } else {
      // If no tooltip, use the latitude and longitude
      inputValue = `${markerLatLng.lat.toString().slice(0, 5)}, ${markerLatLng.lng.toString().slice(0, 5)}`;
    }

    if (!startPoint) {
      startPoint = markerLatLng;
      const startPointInput = document.getElementById("start-point");
      if (startPointInput) {
        startPointInput.value = inputValue;
        startPointInput.setAttribute("title", inputValue);
      }
      const navMess = document.querySelector(".nav-mess");
      if (navMess) {
        navMess.classList.add("hidden");
      }
    } else {
      endPoint = markerLatLng;
      const endPointInput = document.getElementById("end-point");
      if (endPointInput) {
        endPointInput.value = inputValue;
        endPointInput.setAttribute("title", inputValue);
      }
      fetchAndDisplayRoute(startPoint, endPoint);
    }
  }
}

/**
 * Initialize navigation functionality
 * @param {L.Map} mapInstance - The Leaflet map instance
 */
export function initNavigation(mapInstance) {
  window.singleLocationMap = mapInstance;

  // Check if navigation is activated (this should be set by the UI)
  const activateNavigationBtn = document.getElementById("activate-navigation-btn");
  if (activateNavigationBtn) {
    navigationActivated = activateNavigationBtn.textContent.includes("Deactivate");
  }
}

export { clearTempMarkers, fetchAndDisplayRoute };
